/**
 * gpx-fit-room.js — GPX Fit Room UI (theme asset, browser).
 *
 * ===== BEGIN ENGINE MIRROR ================================================
 * Everything down to "===== END ENGINE MIRROR =====" is a byte-for-byte
 * mirror of fitting_room/engine.js (minus the trailing `module.exports`
 * line, which doesn't apply in a plain <script> tag). It is tested via
 * `node fitting_room/test_engine.mjs` against the canonical file — if you
 * change one, change both. See fitting_room/INSTALL.md.
 * ============================================================================
 */
(function () {
  'use strict';

  // Captured synchronously while THIS script is the one executing (works
  // for a `defer`red classic script — document.currentScript is only null
  // for module scripts or code run later from a callback) — used by the
  // FIT-3D loader below to find gpx-fit-room-3d.js's sibling asset URL
  // without needing a liquid change to pass it in explicitly.
  var GPXFR_SELF_SCRIPT_SRC = document.currentScript && document.currentScript.src;

  // ===== BEGIN ENGINE MIRROR =====

  var ANSUR_COEF = {
    male: {
      chest: { intercept: 103.643, height: -0.29094, weight: 0.6235 },
      waist: { intercept: 92.756, height: -0.39612, weight: 0.7587, age: 0.19846 },
      hip: { intercept: 80.841, height: -0.14014, weight: 0.5346 },
    },
    female: {
      chest: { intercept: 99.034, height: -0.32916, weight: 0.7269 },
      waist: { intercept: 96.336, height: -0.45009, weight: 0.9076, age: 0.11424 },
      hip: { intercept: 84.689, height: -0.17841, weight: 0.6861 },
    },
  };

  var SHAPE_STEP = { 'أنحف': -1, 'متوسط': 0, 'أعرض': 1 };
  var CHEST_SHAPE_PCT_CHEST = 0.05;
  var CHEST_SHAPE_PCT_WAIST = 0.07;
  var HIP_SHAPE_PCT_HIP = 0.05;

  function normalizeGender(gender) {
    if (gender === 'ذكر' || gender === 'male') return 'male';
    if (gender === 'أنثى' || gender === 'female') return 'female';
    throw new Error('estimateBody: unknown gender "' + gender + '"');
  }

  function assertRange(name, value, min, max) {
    if (typeof value !== 'number' || isNaN(value)) {
      throw new Error('estimateBody: ' + name + ' must be a number');
    }
    if (value < min || value > max) {
      throw new Error('estimateBody: ' + name + ' out of range (' + min + '-' + max + '): ' + value);
    }
  }

  function estimateBody(p) {
    if (!p || typeof p !== 'object') throw new Error('estimateBody: missing input');
    var sex = normalizeGender(p.gender);
    assertRange('heightCm', p.heightCm, 100, 220);
    assertRange('weightKg', p.weightKg, 30, 200);
    assertRange('age', p.age, 12, 90);

    var chestShape = p.chestShape || 'متوسط';
    var hipShape = p.hipShape || 'متوسط';
    if (!(chestShape in SHAPE_STEP)) throw new Error('estimateBody: unknown chestShape "' + chestShape + '"');
    if (!(hipShape in SHAPE_STEP)) throw new Error('estimateBody: unknown hipShape "' + hipShape + '"');

    var c = ANSUR_COEF[sex];
    var chestBase = c.chest.intercept + c.chest.height * p.heightCm + c.chest.weight * p.weightKg;
    var waistBase =
      c.waist.intercept + c.waist.height * p.heightCm + c.waist.weight * p.weightKg + c.waist.age * p.age;
    var hipBase = c.hip.intercept + c.hip.height * p.heightCm + c.hip.weight * p.weightKg;

    var chestStep = SHAPE_STEP[chestShape];
    var hipStep = SHAPE_STEP[hipShape];

    var chest = chestBase * (1 + chestStep * CHEST_SHAPE_PCT_CHEST);
    var waist = waistBase * (1 + chestStep * CHEST_SHAPE_PCT_WAIST);
    var hip = hipBase * (1 + hipStep * HIP_SHAPE_PCT_HIP);

    return {
      chest: Math.round(Math.max(chest, 40) * 10) / 10,
      waist: Math.round(Math.max(waist, 35) * 10) / 10,
      hip: Math.round(Math.max(hip, 40) * 10) / 10,
      heightCm: p.heightCm,
    };
  }

  // 2026-09-17 addition — mirrors engine.js's own comment: a 4th tier,
  // 'أوفر سايز' ("oversize"), above 'واسع', DESIGNED (not measured) —
  // starts 2cm before واسع's own upper bound (deliberate overlap so
  // scoring prefers the NEXT size up) and keeps واسع's own width.
  var EASE_TARGETS = {
    set: {
      chest: { 'ضيق': [0, 4], 'مظبوط': [4, 8], 'واسع': [8, 14], 'أوفر سايز': [12, 18] },
      waist: { 'ضيق': [0, 4], 'مظبوط': [4, 9], 'واسع': [9, 16], 'أوفر سايز': [14, 21] },
      hip: { 'ضيق': [0, 4], 'مظبوط': [4, 9], 'واسع': [9, 15], 'أوفر سايز': [13, 19] },
    },
    pyjama: {
      chest: { 'ضيق': [4, 8], 'مظبوط': [8, 14], 'واسع': [14, 22], 'أوفر سايز': [20, 28] },
      waist: { 'ضيق': [4, 8], 'مظبوط': [8, 15], 'واسع': [15, 24], 'أوفر سايز': [22, 31] },
      hip: { 'ضيق': [4, 8], 'مظبوط': [8, 15], 'واسع': [15, 23], 'أوفر سايز': [21, 29] },
    },
  };

  var ZONE_LABEL = { chest: 'الصدر', waist: 'الوسط', hip: 'الهيب', length: 'الطول' };
  var ZONES = ['chest', 'waist', 'hip'];
  var FIT_PREFS = ['ضيق', 'مظبوط', 'واسع', 'أوفر سايز'];

  // rev6 (2026-09-17) — mannequin style + colour switcher (owner's build
  // order, verbatim: «استخدم برضو دمية الخياطة ويكون في في الجنب كدا
  // اختيارات انه يغير شكل المانيكان وكمان خلي في امكانيه انه يغير
  // لونه»). Kept as plain data here (UI + persistence) — the actual 3D
  // material/geometry work lives in gpx-fit-room-3d.js's applyStyle/
  // applyColor, mirrored here only as the list of valid keys so a
  // corrupted localStorage value can be rejected the same way
  // FIT_PREFS.indexOf(...) already guards fitPreference below.
  var MANNEQUIN_STYLES = [
    {
      key: 'glossy', label: 'مانيكان',
      // Small round head + shoulders silhouette.
      icon: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="6.5" r="3.4"/><path d="M4.5 20.5c0-4.7 3.4-8 7.5-8s7.5 3.3 7.5 8" /></svg>',
    },
    {
      key: 'dress-form', label: 'دمية خياط',
      // Headless torso capped by a small knob, on a thin pole — a tiny
      // dress-form silhouette.
      icon: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="10.3" y="2.4" width="3.4" height="2.2" rx="1"/><path d="M6 8.2c0-2.6 2.7-4.4 6-4.4s6 1.8 6 4.4-1.7 7.6-6 7.6-6-5-6-7.6Z"/><line x1="12" y1="15.8" x2="12" y2="21.5"/><line x1="9" y1="21.5" x2="15" y2="21.5"/></svg>',
    },
  ];
  var MANNEQUIN_STYLE_KEYS = MANNEQUIN_STYLES.map(function (s) { return s.key; });
  var DEFAULT_MANNEQUIN_STYLE = 'glossy';
  // rev7 (2026-09-20, owner verbatim: «هو تمام بس لونه اسود بس — عاوز
  // اللون يكون ابيض وشيل تغييرات الالوان») — the colour switcher (6
  // swatches, including a literal black #1c1c1c "أسود" option) is REMOVED
  // entirely. ROOT CAUSE of the reported black render: this array's
  // DEFAULT_MANNEQUIN_COLOR was '#f4f1ec' (a light off-white — "أبيض
  // لامع"/glossy-white, the FIRST swatch, not a bug by itself), but a
  // shopper who at any point clicked the LAST swatch ("أسود", #1c1c1c)
  // had that choice persisted via persistMannequinPrefs() into
  // localStorage (STORAGE_KEY gpxFitProfile_v1 → mannequinColor). On
  // every later visit, savedMannequinColor (below) read that saved value
  // back and it silently outranked the default from then on — so "black"
  // was never a code defect, it was a real previous pick surviving in
  // the shopper's own browser storage. There is no colour concept left
  // to save or prefill any more — the mannequin is now permanently white,
  // fixed in gpx-fit-room-3d.js's DEFAULT_BODY_COLOR (#f7f5f2), never
  // overridden by anything from this file.

  var LENGTH_RATIO = { set: 0.6, pyjama: 0.615 };
  var LENGTH_EASE_TOLERANCE_CM = 3;

  function garmentKey(garment) {
    var g = String(garment || '').toLowerCase();
    if (g === 'pyjama' || g === 'بيجامة') return 'pyjama';
    return 'set';
  }

  function midpoint(range) {
    return (range[0] + range[1]) / 2;
  }

  function scoreZone(easeCm, garmentKind, zone, fitPreference) {
    var bands = EASE_TARGETS[garmentKind][zone];
    var target = bands[fitPreference];
    var center = midpoint(target);
    var penalty;
    if (easeCm < target[0]) {
      penalty = target[0] - easeCm;
    } else if (easeCm > target[1]) {
      penalty = easeCm - target[1];
    } else {
      penalty = Math.abs(easeCm - center) * 0.15;
    }

    var regular = bands['مظبوط'];
    var verdict;
    if (easeCm < regular[0]) verdict = 'ضيق';
    else if (easeCm > regular[1]) verdict = 'واسع';
    else verdict = 'مظبوط';

    return { penalty: penalty, verdict: verdict, easeCm: easeCm };
  }

  function garmentCircumference(rawValue, measureType) {
    if (measureType === 'half') return rawValue * 2;
    return rawValue;
  }

  function normalizeSizeKey(s) {
    return String(s).trim().toUpperCase();
  }

  function recommend(body, chart, garment, fitPreference, availabilityBySize) {
    if (!chart || !chart.sizes || typeof chart.sizes !== 'object') {
      throw new Error('recommend: chart.sizes is required');
    }
    if (FIT_PREFS.indexOf(fitPreference) === -1) {
      throw new Error('recommend: unknown fitPreference "' + fitPreference + '"');
    }
    var garmentKind = garmentKey(garment || chart.garment);
    var measureType = chart.measure_type || {};
    var sizeNames = Object.keys(chart.sizes);
    if (sizeNames.length === 0) {
      throw new Error('recommend: chart has no sizes');
    }

    var liveAvailability = null;
    if (availabilityBySize && typeof availabilityBySize === 'object') {
      liveAvailability = {};
      Object.keys(availabilityBySize).forEach(function (k) {
        liveAvailability[normalizeSizeKey(k)] = !!availabilityBySize[k];
      });
    }

    var scored = sizeNames.map(function (sizeName) {
      var entry = chart.sizes[sizeName];
      var zoneResults = {};
      var totalPenalty = 0;
      var zoneCount = 0;
      ZONES.forEach(function (zone) {
        if (entry[zone] === undefined || entry[zone] === null) return;
        if (body[zone] === undefined || body[zone] === null) return;
        var circumference = garmentCircumference(entry[zone], measureType[zone]);
        var easeCm = circumference - body[zone];
        var scored2 = scoreZone(easeCm, garmentKind, zone, fitPreference);
        zoneResults[zone] = { verdict: scored2.verdict, easeCm: Math.round(easeCm * 10) / 10, label: ZONE_LABEL[zone] };
        totalPenalty += scored2.penalty;
        zoneCount += 1;
      });
      var avgPenalty = zoneCount > 0 ? totalPenalty / zoneCount : Infinity;
      var available;
      if (liveAvailability) {
        var key = normalizeSizeKey(sizeName);
        available = Object.prototype.hasOwnProperty.call(liveAvailability, key) ? liveAvailability[key] : false;
      } else {
        available = entry.available !== false;
      }
      return {
        sizeName: sizeName,
        avgPenalty: avgPenalty,
        zoneCount: zoneCount,
        zoneResults: zoneResults,
        available: available,
      };
    });

    var comparable = scored.filter(function (s) { return s.zoneCount > 0; });
    if (comparable.length === 0) {
      throw new Error('recommend: chart has no zones in common with the body measurements');
    }

    comparable.sort(function (a, b) { return a.avgPenalty - b.avgPenalty; });

    var best = comparable[0];

    var chosen = best;
    var soldOutFallback = false;
    var soldOutSize = null;
    if (!best.available) {
      soldOutSize = best.sizeName;
      var nextAvailable = null;
      for (var i = 0; i < comparable.length; i++) {
        if (comparable[i].available) { nextAvailable = comparable[i]; break; }
      }
      if (nextAvailable) {
        chosen = nextAvailable;
        soldOutFallback = true;
      }
    }

    // Runner-up: next best-fitting size that is BOTH available and not the
    // chosen one — never a sold-out size (2026-09-16 fix; previously this
    // was just comparable[1] regardless of stock, which is exactly the bug
    // reported on بيجامه حريمي شتوي قطعتين: a sold-out runner-up offered).
    var runnerUp = null;
    for (var j = 0; j < comparable.length; j++) {
      if (comparable[j].sizeName !== chosen.sizeName && comparable[j].available) {
        runnerUp = comparable[j];
        break;
      }
    }

    var outOfStock = !chosen.available;

    var zoneCoverage = chosen.zoneCount / ZONES.length;
    var separation = 1;
    if (runnerUp) {
      var gap = Math.abs(runnerUp.avgPenalty - best.avgPenalty);
      separation = Math.min(1, gap / 3);
    }
    var confidence = Math.round(Math.max(0.2, Math.min(0.97, 0.5 * zoneCoverage + 0.5 * separation + 0.15)) * 100) / 100;

    var note;
    if (chosen.zoneCount < ZONES.length) {
      note = 'المقاس ده مبني على القياسات المتاحة في الجدول بس — مش كل المناطق متغطية، فالنتيجة تقريبية.';
    } else {
      note = 'المقاس ده تقدير مبني على بياناتك ومقاسات المنتج — لو بينك مقاسين، جرّب الاتنين.';
    }
    if (soldOutFallback) {
      note = 'مقاسك المثالي ' + soldOutSize + ' بس خلصان — أقرب متاح ' + chosen.sizeName + '. ' + note;
    } else if (outOfStock) {
      note = 'المنتج ده خلص من المخزون في كل المقاسات حالياً — مفيش مقاس نقدر نعرضه.';
    }

    var chosenEntry = chart.sizes[chosen.sizeName];
    if (chosenEntry && chosenEntry.length != null && typeof body.heightCm === 'number') {
      var idealLength = body.heightCm * (LENGTH_RATIO[garmentKind] || LENGTH_RATIO.set);
      var diffCm = Math.round((chosenEntry.length - idealLength) * 10) / 10;
      var lengthVerdict;
      if (diffCm < -LENGTH_EASE_TOLERANCE_CM) lengthVerdict = 'ضيق';
      else if (diffCm > LENGTH_EASE_TOLERANCE_CM) lengthVerdict = 'واسع';
      else lengthVerdict = 'مظبوط';
      chosen.zoneResults.length = { verdict: lengthVerdict, easeCm: diffCm, label: ZONE_LABEL.length };
    }

    // rev 2026-09-17: mirrors engine.js — what 'مظبوط' would have picked,
    // only when it differs from the preference actually requested (drives
    // "لو عايزه عادي: L" on the result screen).
    var alternateRegularSize = null;
    if (fitPreference !== 'مظبوط') {
      var regular = recommend(body, chart, garment, 'مظبوط', availabilityBySize);
      if (regular.size && regular.size !== chosen.sizeName) alternateRegularSize = regular.size;
    }

    return {
      size: chosen.sizeName,
      runnerUp: runnerUp ? runnerUp.sizeName : null,
      zones: chosen.zoneResults,
      confidence: confidence,
      note: note,
      soldOutFallback: soldOutFallback,
      idealSoldOutSize: soldOutFallback ? soldOutSize : null,
      available: chosen.available,
      outOfStock: outOfStock,
      fitPreference: fitPreference,
      alternateRegularSize: alternateRegularSize,
    };
  }

  // FIT-MANNEQUIN mirror (2026-09-16) — byte-for-byte copy of engine.js
  // sections 4 & 5 (MEASURE_BOUNDS/clampMeasurement/correctionZonesForGender/
  // applyMeasurementCorrections, and the mannequin geometry). See engine.js
  // for the full comments on where every number comes from.
  var MEASURE_BOUNDS = {
    chest: [55, 170],
    waist: [45, 170],
    hip: [55, 170],
  };

  function clampMeasurement(zone, value) {
    var bounds = MEASURE_BOUNDS[zone];
    if (!bounds) return value;
    var num = Number(value);
    if (!isFinite(num) || isNaN(num)) return bounds[0];
    return Math.min(bounds[1], Math.max(bounds[0], num));
  }

  function correctionZonesForGender(gender) {
    var isFemale = gender === 'أنثى' || gender === 'female';
    return isFemale ? ['chest', 'waist', 'hip'] : ['chest', 'waist'];
  }

  function applyMeasurementCorrections(body, corrections) {
    var out = {};
    Object.keys(body).forEach(function (k) { out[k] = body[k]; });
    if (!corrections || typeof corrections !== 'object') return out;
    ['chest', 'waist', 'hip'].forEach(function (zone) {
      var raw = corrections[zone];
      if (raw === undefined || raw === null || raw === '') return;
      var num = Number(raw);
      if (!isFinite(num) || isNaN(num)) return;
      out[zone] = clampMeasurement(zone, num);
    });
    return out;
  }

  function seedCorrectionsFromEstimate(gender, body) {
    var out = {};
    if (!body) return out;
    correctionZonesForGender(gender).forEach(function (zone) {
      if (typeof body[zone] !== 'number' || !isFinite(body[zone])) return;
      out[zone] = clampMeasurement(zone, Math.round(body[zone]));
    });
    return out;
  }

  // ----- Mannequin geometry (engine.js section 5) — 2026-09-16 rebuild:
  // Catmull-Rom splines through named landmarks, split into torso/arm/leg
  // shapes, instead of one hand-tuned cubic-Bezier outline. See engine.js
  // for the full "why" comment (the old version read as a pinched vase).
  var MANNEQUIN_VIEW = { w: 140, h: 250 };
  var HALF_WIDTH_PX_RANGE = [15, 40];
  var HALF_WIDTH_CM_RANGE = [45, 170];

  function cmToHalfWidth(cm) {
    var lo = HALF_WIDTH_CM_RANGE[0];
    var hi = HALF_WIDTH_CM_RANGE[1];
    var c = Math.min(hi, Math.max(lo, Number(cm)));
    var t = (c - lo) / (hi - lo);
    return HALF_WIDTH_PX_RANGE[0] + t * (HALF_WIDTH_PX_RANGE[1] - HALF_WIDTH_PX_RANGE[0]);
  }

  var LEG_LEN_PX_RANGE = [78, 118];
  var HEIGHT_CM_RANGE = [140, 200];

  function cmToFigureHeight(heightCm) {
    var lo = HEIGHT_CM_RANGE[0];
    var hi = HEIGHT_CM_RANGE[1];
    var h = Math.min(hi, Math.max(lo, Number(heightCm) || 170));
    var t = (h - lo) / (hi - lo);
    return LEG_LEN_PX_RANGE[0] + t * (LEG_LEN_PX_RANGE[1] - LEG_LEN_PX_RANGE[0]);
  }

  function mannequinMetrics(gender, measures) {
    var m = measures || {};
    var isFemale = gender === 'أنثى' || gender === 'female';
    var chestHW = cmToHalfWidth(m.chest != null ? m.chest : 95);
    var waistHW = cmToHalfWidth(m.waist != null ? m.waist : 80);
    var hipHW = cmToHalfWidth(m.hip != null ? m.hip : 95);
    var legLen = cmToFigureHeight(m.heightCm);
    var shoulderHW = chestHW * (isFemale ? 0.8 : 1.06);
    var cx = MANNEQUIN_VIEW.w / 2;
    var shoulderY = 46;
    var chestY = isFemale ? 74 : 70;
    var waistY = 114;
    var hipY = isFemale ? 140 : 134;
    var crotchY = hipY + 12;
    var kneeY = crotchY + legLen * 0.5;
    var ankleY = crotchY + legLen * 0.96;
    var footY = ankleY + 6;
    var zoneY = { chest: chestY, waist: waistY, hip: hipY };
    return {
      isFemale: isFemale, cx: cx, chestHW: chestHW, waistHW: waistHW, hipHW: hipHW, shoulderHW: shoulderHW, legLen: legLen,
      shoulderY: shoulderY, chestY: chestY, waistY: waistY, hipY: hipY, crotchY: crotchY, kneeY: kneeY, ankleY: ankleY, footY: footY,
      zoneY: zoneY
    };
  }

  function catmullRomClosed(points) {
    var n = points.length;
    function at(i) { return points[((i % n) + n) % n]; }
    function r(v) { return Math.round(v * 10) / 10; }
    var d = 'M ' + r(points[0][0]) + ' ' + r(points[0][1]) + ' ';
    for (var i = 0; i < n; i++) {
      var p0 = at(i - 1);
      var p1 = at(i);
      var p2 = at(i + 1);
      var p3 = at(i + 2);
      var c1x = p1[0] + (p2[0] - p0[0]) / 6;
      var c1y = p1[1] + (p2[1] - p0[1]) / 6;
      var c2x = p2[0] - (p3[0] - p1[0]) / 6;
      var c2y = p2[1] - (p3[1] - p1[1]) / 6;
      d += 'C ' + r(c1x) + ' ' + r(c1y) + ' ' + r(c2x) + ' ' + r(c2y) + ' ' + r(p2[0]) + ' ' + r(p2[1]) + ' ';
    }
    return d + 'Z';
  }

  function buildMannequinShapes(metrics) {
    var m = metrics;
    var cx = m.cx;

    var torso = catmullRomClosed([
      [cx + 6, m.shoulderY - 6],
      [cx + m.shoulderHW, m.shoulderY],
      [cx + m.chestHW, m.chestY],
      [cx + m.waistHW, m.waistY],
      [cx + m.hipHW, m.hipY],
      [cx + m.hipHW * 0.42, m.crotchY],
      [cx, m.crotchY - 4],
      [cx - m.hipHW * 0.42, m.crotchY],
      [cx - m.hipHW, m.hipY],
      [cx - m.waistHW, m.waistY],
      [cx - m.chestHW, m.chestY],
      [cx - m.shoulderHW, m.shoulderY],
      [cx - 6, m.shoulderY - 6],
    ]);

    function arm(sign) {
      var armLen = m.hipY - m.shoulderY + 8;
      var thick = Math.max(7, m.chestHW * 0.16);
      var thickBottom = thick * 0.75;
      var topX = cx + sign * (m.shoulderHW - 3);
      var topY = m.shoulderY + 2;
      var elbowY = m.shoulderY + armLen * 0.52;
      var wristY = m.shoulderY + armLen;
      var outerElbowX = cx + sign * (m.shoulderHW + thick * 0.5);
      var outerWristX = cx + sign * (m.hipHW * 0.62);
      var innerElbowX = cx + sign * (m.shoulderHW - thick * 0.6);
      var innerWristX = outerWristX - sign * thickBottom * 1.4;
      return catmullRomClosed([
        [topX, topY],
        [outerElbowX, elbowY],
        [outerWristX, wristY],
        [innerWristX, wristY],
        [innerElbowX, elbowY],
        [cx + sign * (m.shoulderHW - 9), m.shoulderY + 5],
      ]);
    }

    function leg(sign) {
      var hipInnerX = cx + sign * (m.hipHW * 0.4);
      var hipOuterX = cx + sign * (m.hipHW * 0.92);
      var thighHW = m.hipHW * 0.8;
      var kneeHW = m.hipHW * 0.44;
      var calfHW = m.hipHW * 0.48;
      var ankleHW = m.hipHW * 0.26;
      var midThighY = m.crotchY + (m.kneeY - m.crotchY) * 0.5;
      var midCalfY = m.kneeY + (m.ankleY - m.kneeY) * 0.45;
      return catmullRomClosed([
        [hipInnerX, m.crotchY],
        [hipOuterX, m.crotchY - 1],
        [cx + sign * thighHW, midThighY],
        [cx + sign * kneeHW, m.kneeY],
        [cx + sign * calfHW, midCalfY],
        [cx + sign * ankleHW, m.ankleY],
        [cx + sign * ankleHW * 0.35, m.ankleY],
        [cx + sign * calfHW * 0.32, midCalfY],
        [cx + sign * kneeHW * 0.32, m.kneeY],
        [cx + sign * thighHW * 0.32, midThighY],
      ]);
    }

    return {
      torso: torso,
      armLeft: arm(-1),
      armRight: arm(1),
      legLeft: leg(-1),
      legRight: leg(1),
    };
  }

  function mannequinFootEllipse(metrics, sign) {
    var ankleHW = metrics.hipHW * 0.26;
    return {
      cx: metrics.cx + sign * ankleHW * 0.6,
      cy: metrics.ankleY,
      rx: ankleHW * 1.1,
      ry: 5,
    };
  }

  // chartCoverageGap — mirrors engine.js's chartCoverageGap() byte-for-byte
  // (see engine.js's own comment for why this stayed a separate function
  // instead of folding into recommend()). Added 2026-09-15 for the real
  // chart import — see optimization/fitting_room/DATA_REPORT_2026-09-15.md.
  function chartCoverageGap(chart, productSizes) {
    var chartSizes = Object.keys((chart && chart.sizes) || {});
    var chartSetUpper = {};
    chartSizes.forEach(function (s) {
      chartSetUpper[String(s).trim().toUpperCase()] = true;
    });
    var missingSizes = [];
    (productSizes || []).forEach(function (ps) {
      var norm = String(ps).trim().toUpperCase();
      if (!chartSetUpper[norm] && missingSizes.indexOf(ps) === -1) {
        missingSizes.push(ps);
      }
    });
    return { hasGap: missingSizes.length > 0, missingSizes: missingSizes, chartSizes: chartSizes };
  }

  // ===== END ENGINE MIRROR =====

  // ==========================================================================
  // UI
  // ==========================================================================

  var STORAGE_KEY = 'gpxFitProfile_v1';
  var ZONE_ORDER = ['chest', 'waist', 'hip', 'length'];

  // rev5.1 (real-staging review): a real saved profile was found with
  // chest=55, waist=119, hip=170 — each value individually inside
  // MEASURE_BOUNDS (so clampMeasurement alone lets every one of them
  // through), but jointly anatomically impossible (no real torso has a
  // waist 64cm bigger than its chest). Root cause almost certainly a
  // manual slider drag during testing that got saved as if it were a
  // real correction (see clampMeasurement's own regression-guard comment
  // for the earlier, related "chest=150 default" false alarm). Discards
  // the WHOLE corrections object (not just the offending zone) when any
  // pair fails a basic real-body plausibility check, so
  // computeEstimateAndSeedCorrections() re-seeds every zone fresh from
  // the ANSUR estimate instead of keeping any of the surviving garbage.
  function sanitizeSavedCorrections(corrections) {
    if (!corrections || typeof corrections !== 'object') return {};
    var out = {};
    ['chest', 'waist', 'hip'].forEach(function (zone) {
      var raw = corrections[zone];
      if (raw === undefined || raw === null || raw === '') return;
      var num = Number(raw);
      if (!isFinite(num) || isNaN(num)) return;
      out[zone] = clampMeasurement(zone, num);
    });
    if (out.chest != null && out.waist != null && Math.abs(out.chest - out.waist) > 40) return {};
    if (out.waist != null && out.hip != null && Math.abs(out.hip - out.waist) > 45) return {};
    if (out.chest != null && out.hip != null && Math.abs(out.chest - out.hip) > 45) return {};
    return out;
  }

  function safeGetProfile() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var profile = JSON.parse(raw);
      if (profile && typeof profile === 'object') {
        profile.corrections = sanitizeSavedCorrections(profile.corrections);
      }
      return profile;
    } catch (e) {
      return null;
    }
  }

  function safeSetProfile(profile) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    } catch (e) {
      /* private mode / quota / disabled storage — silently skip, no crash */
    }
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    attrs = attrs || {};
    Object.keys(attrs).forEach(function (k) {
      if (k === 'text') node.textContent = attrs[k];
      else if (k === 'html') node.innerHTML = attrs[k];
      else node.setAttribute(k, attrs[k]);
    });
    (children || []).forEach(function (c) { if (c) node.appendChild(c); });
    return node;
  }

  // 2026-09-16: the old discrete أنحف/متوسط/أعرض silhouette-icon picker
  // (silhouetteSvg/shapePicker/SHAPE_LABELS) is GONE — replaced by the
  // Sizebay-style growing/shrinking mannequin + cm sliders below
  // (mannequinMetrics/buildMannequinShapes, mirrored from engine.js, and
  // renderStep2's slider rows). Removed rather than left dead: the
  // shopper's own corrected cm number is strictly more informative than a
  // ±5-7% shape step, and the brief asked for this step to feel like
  // Sizebay's measurement screen, not to keep both side by side.

  // --------------------------------------------------------------------
  // FIT-3D loader — lazy-injects gpx-fit-room-3d.js (a small theme asset,
  // our own code) the FIRST time any modal instance on the page reaches
  // step 2/3, never on normal page load. That file itself lazy-loads the
  // actual three.js library from a CDN — see its own header comment. If
  // this ever fails (asset 404, ancient browser with no dynamic
  // `import()`), gpxfr3dDisabled is set once and every instance on the
  // page just keeps showing the SVG mannequin — no retry storms, no
  // broken flow.
  // --------------------------------------------------------------------
  var gpxfr3dScriptState = 'idle'; // idle | loading | ready | failed
  var gpxfr3dCallbacks = [];
  var gpxfr3dDisabled = false;

  function gpxfr3dAssetUrl() {
    // Preferred: layout/theme.liquid emits this as a plain global with
    // gpx-fit-room-3d.js's OWN correct, always-fresh Shopify asset
    // fingerprint (2026-09-16 fix — see that file's comment for the full
    // story: guessing the sibling URL by swapping the filename in
    // gpx-fit-room.js's OWN script src reused gpx-fit-room.js's
    // fingerprint, so a CDN/proxy cache keyed by full URL kept serving a
    // stale pre-fix copy of gpx-fit-room-3d.js even after redeploying it —
    // a real live bug this global eliminates by construction). The
    // currentScript-derived guess stays as a fallback for a cached page
    // that predates this liquid change.
    if (typeof window.GPXFR_3D_ASSET_URL === 'string' && window.GPXFR_3D_ASSET_URL) {
      return window.GPXFR_3D_ASSET_URL;
    }
    if (!GPXFR_SELF_SCRIPT_SRC) return null;
    if (GPXFR_SELF_SCRIPT_SRC.indexOf('gpx-fit-room.js') === -1) return null;
    return GPXFR_SELF_SCRIPT_SRC.replace('gpx-fit-room.js', 'gpx-fit-room-3d.js');
  }

  function loadMannequin3DScript(cb) {
    if (gpxfr3dDisabled) return;
    if (gpxfr3dScriptState === 'ready') { cb(); return; }
    if (gpxfr3dScriptState === 'failed') { gpxfr3dDisabled = true; return; }
    gpxfr3dCallbacks.push(cb);
    if (gpxfr3dScriptState === 'loading') return;
    var url = gpxfr3dAssetUrl();
    if (!url) { gpxfr3dDisabled = true; gpxfr3dCallbacks = []; return; }
    gpxfr3dScriptState = 'loading';
    var s = document.createElement('script');
    s.src = url;
    s.async = true;
    s.onload = function () {
      gpxfr3dScriptState = 'ready';
      var cbs = gpxfr3dCallbacks;
      gpxfr3dCallbacks = [];
      cbs.forEach(function (fn) { fn(); });
    };
    s.onerror = function () {
      gpxfr3dScriptState = 'failed';
      gpxfr3dDisabled = true;
      gpxfr3dCallbacks = [];
    };
    document.head.appendChild(s);
  }

  function init(root) {
    if (root.dataset.gpxFrInit) return;
    root.dataset.gpxFrInit = '1';

    // rev5: mesh asset URLs (permanent Shopify Files GenericFile CDN URLs —
    // theme-asset tooling can't hold raw binary .glb, so these ship as plain
    // data attributes on the snippet markup instead of hardcoded in JS).
    // Only set the global once (first modal instance wins; harmless if a
    // page renders this snippet more than once with identical URLs).
    if (root.dataset.gpxFrMeshMale && typeof window.GPXFR_MESH_ASSET_URL_MALE !== 'string') {
      window.GPXFR_MESH_ASSET_URL_MALE = root.dataset.gpxFrMeshMale;
    }
    if (root.dataset.gpxFrMeshFemale && typeof window.GPXFR_MESH_ASSET_URL_FEMALE !== 'string') {
      window.GPXFR_MESH_ASSET_URL_FEMALE = root.dataset.gpxFrMeshFemale;
    }

    var openBtn = root.querySelector('[data-gpx-fr-open]');
    var veil = root.querySelector('[data-gpx-fr-veil]');
    var closeBtn = root.querySelector('[data-gpx-fr-close]');
    var bodyEl = root.querySelector('[data-gpx-fr-body]');
    var modal = root.querySelector('.gpx-fr__modal');

    var chartScript = root.querySelector('[data-gpx-fr-chart]');
    var variantsScript = root.querySelector('[data-gpx-fr-variants]');
    var chart = null;
    var variants = [];
    try { chart = JSON.parse(chartScript.textContent); } catch (e) { chart = null; }
    try { variants = JSON.parse(variantsScript.textContent) || []; } catch (e) { variants = []; }

    if (!chart || !chart.sizes) return; // nothing usable — button already hidden server-side, but guard anyway

    // Move the veil to be a direct child of <body> — 2026-09-16 real-Chrome
    // fix. The live theme wraps the PDP form in its own sticky-info wrapper
    // (.t4s-product__info-container--sticky) that carries a CSS transform
    // (even an identity one) for its own animation; per the CSS spec ANY
    // transformed ancestor creates a new containing block for
    // `position:fixed` descendants, so our "fixed, full-viewport" veil was
    // rendering trapped inside that ancestor's box (~550px, anchored near
    // the trigger) instead of covering the real ~1500px viewport — seen
    // live as a small dark box instead of a centered full-screen modal.
    // Reparenting is the standard fix; gpx-fit-room.css duplicates the
    // --gpx-fr-* custom properties onto .gpx-fr__veil itself so it keeps
    // its tokens after leaving the .gpx-fr subtree.
    if (veil.parentElement !== document.body) {
      document.body.appendChild(veil);
    }

    // All distinct sizes this PRODUCT actually sells (from live variants,
    // not the chart) — used only to warn honestly when the shopper's real
    // size might be one the chart has no row for. See chartCoverageGap().
    var productSizes = [];
    variants.forEach(function (v) {
      if (v.size && productSizes.indexOf(v.size) === -1) productSizes.push(v.size);
    });
    var coverageGap = chartCoverageGap(chart, productSizes);

    // LIVE per-size stock, fed into recommend() so it never recommends (or
    // offers as a runner-up) a size that's actually sold out — 2026-09-16
    // fix, see optimization/store_qa/QA_2026-09-16.md. Uses the SAME "first
    // matching variant wins" convention as findVariantForSize() below, so
    // what recommend() decides is available always matches what clicking
    // the size button will actually select.
    var availabilityBySize = {};
    variants.forEach(function (v) {
      if (!v.size) return;
      var key = v.size.trim().toUpperCase();
      if (!Object.prototype.hasOwnProperty.call(availabilityBySize, key)) {
        availabilityBySize[key] = !!v.available;
      }
    });

    // rev5.2 (owner, verbatim): «غرفة القياس تبدأ افتراضياً علطول بالطول
    // وهكذا ثم المانيكان» — every open starts at step 1 (height/weight/…,
    // now also the fit-preference cards — see FIT_PREF_CARDS below),
    // never jumps straight to step 2 from a saved profile. A saved profile
    // may PREFILL the step-1 inputs (gender/height/weight/age/fitPreference)
    // — nothing more: corrections always start fresh so step 2 re-seeds
    // them from a real ANSUR estimate of THIS session's inputs, never from
    // a stale saved correction.
    var saved = safeGetProfile();
    var savedFitPref = saved && FIT_PREFS.indexOf(saved.fitPreference) !== -1 ? saved.fitPreference : 'مظبوط';
    // rev6 — style prefill ONLY, same guarded pattern as savedFitPref
    // above: a corrupted/stale localStorage value never reaches the 3D
    // layer, it just falls back to the default. rev7 (owner: «شيل
    // تغييرات الالوان») — colour prefill REMOVED: a saved profile's
    // `mannequinColor` field (from before this fix, e.g. a previously
    // picked dark swatch) is simply never read any more — see the
    // DEFAULT_MANNEQUIN_STYLE comment above for why that was the actual
    // root cause of the reported black mannequin.
    var savedMannequinStyle = saved && MANNEQUIN_STYLE_KEYS.indexOf(saved.mannequinStyle) !== -1
      ? saved.mannequinStyle : DEFAULT_MANNEQUIN_STYLE;
    var state = {
      step: 1,
      profile: saved
        ? { gender: saved.gender, heightCm: saved.heightCm, weightKg: saved.weightKg, age: saved.age }
        : { gender: 'ذكر', heightCm: '', weightKg: '', age: '' },
      corrections: {},
      activeZone: 'chest',
      estimate: null,
      fitPreference: savedFitPref,
      mannequinStyle: savedMannequinStyle,
      result: null,
    };

    var lastFocused = null;

    // rev6 — persists the style choice, merged onto whatever profile is
    // already in localStorage (safeGetProfile(), not the in-memory
    // `state`) — deliberately NOT the goBtn flow's full safeSetProfile()
    // below, which would stomp a previously saved real `corrections`
    // object with state.corrections's fresh-per-session {} if the
    // shopper clicks a style chip before ever finishing step 2. Called
    // from the style switcher's chip handler in renderStep2. rev7: no
    // longer persists a colour — any `mannequinColor` field left over on
    // `existing` from before this fix is harmlessly ignored (never read
    // back; see savedMannequinStyle above), not stripped here, since
    // touching unrelated saved fields isn't this function's job.
    function persistMannequinPrefs() {
      var existing = safeGetProfile() || {};
      existing.gender = state.profile.gender;
      existing.heightCm = state.profile.heightCm;
      existing.weightKg = state.profile.weightKg;
      existing.age = state.profile.age;
      existing.fitPreference = state.fitPreference;
      existing.mannequinStyle = state.mannequinStyle;
      safeSetProfile(existing);
    }

    // The live FIT-3D controller for whichever step (2 or 3) currently has
    // one mounted — at most one at a time per modal instance. Destroyed
    // before every re-render()/close so a WebGL context never leaks across
    // step transitions (mobile browsers cap concurrent contexts).
    var mannequin3D = null;
    function destroyMannequin3D() {
      if (mannequin3D) {
        try { mannequin3D.destroy(); } catch (e) { /* noop */ }
        mannequin3D = null;
      }
    }

    function openModal() {
      lastFocused = document.activeElement;
      veil.hidden = false;
      document.body.style.overflow = 'hidden';
      render();
      var firstFocusable = modal.querySelector('input,button,[tabindex]');
      if (firstFocusable) firstFocusable.focus();
      document.addEventListener('keydown', onKeydown, true);
    }

    function closeModal() {
      veil.hidden = true;
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown, true);
      destroyMannequin3D();
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeModal();
        return;
      }
      if (e.key === 'Tab') {
        var focusables = modal.querySelectorAll('input,button,[tabindex]:not([tabindex="-1"])');
        if (!focusables.length) return;
        var firstEl = focusables[0];
        var lastEl = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }

    openBtn.addEventListener('click', openModal);
    closeBtn.addEventListener('click', closeModal);
    veil.addEventListener('click', function (e) { if (e.target === veil) closeModal(); });

    function stepDots(activeStep, total) {
      var wrap = el('div', { class: 'gpx-fr__steps' });
      for (var i = 1; i <= total; i++) {
        var cls = 'gpx-fr__step-dot' + (i === activeStep ? ' is-active' : i < activeStep ? ' is-done' : '');
        wrap.appendChild(el('span', { class: cls }));
      }
      return wrap;
    }

    function validateProfile(p) {
      var errors = {};
      var heightCm = Number(p.heightCm);
      var weightKg = Number(p.weightKg);
      var age = Number(p.age);
      if (!p.heightCm || isNaN(heightCm) || heightCm < 100 || heightCm > 220) {
        errors.heightCm = 'الطول لازم يكون بين 100 و220 سم';
      }
      if (!p.weightKg || isNaN(weightKg) || weightKg < 30 || weightKg > 200) {
        errors.weightKg = 'الوزن لازم يكون بين 30 و200 كجم';
      }
      if (!p.age || isNaN(age) || age < 12 || age > 90) {
        errors.age = 'السن لازم يكون بين 12 و90 سنة';
      }
      return errors;
    }

    // Computes the ANSUR estimate from step-1 data and, for every zone
    // step 2 shows for the current gender, seeds state.corrections[zone]
    // with seedCorrectionsFromEstimate()'s value ONLY if that zone has no
    // value yet — so a shopper's own earlier correction is never silently
    // reset back to the regression guess by re-entering step 2. Uses the
    // SAME seedCorrectionsFromEstimate() function engine.js's test suite
    // pins against real estimateBody() output (2026-09-16 default-value
    // regression guard — see engine.js's doc comment on that function for
    // why: a live proof screenshot appeared to show chest=150 as a default,
    // traced to a manual test-slider drag, not a real bug, but this keeps
    // it that way for good).
    function computeEstimateAndSeedCorrections() {
      try {
        state.estimate = estimateBody({
          gender: state.profile.gender,
          heightCm: state.profile.heightCm,
          weightKg: state.profile.weightKg,
          age: state.profile.age,
        });
      } catch (e) {
        state.estimate = null;
      }
      if (!state.estimate) return;
      var seeded = seedCorrectionsFromEstimate(state.profile.gender, state.estimate);
      Object.keys(seeded).forEach(function (zone) {
        var current = state.corrections[zone];
        if (current === undefined || current === null || current === '') {
          state.corrections[zone] = seeded[zone];
        }
      });
    }

    // rev5.2: every modal open now always starts at step 1 (see state
    // init above) — the estimate/corrections are seeded once the shopper
    // actually reaches step 2 (renderStep2 already calls
    // computeEstimateAndSeedCorrections() itself if state.estimate is
    // still null), so no up-front seed is needed here any more.

    function renderStep1() {
      var wrap = el('div', { class: 'gpx-fr__step' });
      wrap.appendChild(stepDots(1, 2));
      wrap.appendChild(el('h3', { class: 'gpx-fr__step-heading', text: 'بياناتك الأساسية' }));

      var genderGroup = el('div', { class: 'gpx-fr__pill-group', role: 'radiogroup', 'aria-label': 'الجنس' });
      ['ذكر', 'أنثى'].forEach(function (g) {
        var pressed = state.profile.gender === g;
        var btn = el('button', { type: 'button', class: 'gpx-fr__pill', 'aria-pressed': pressed ? 'true' : 'false', text: g });
        btn.addEventListener('click', function () {
          state.profile.gender = g;
          render();
        });
        genderGroup.appendChild(btn);
      });
      wrap.appendChild(el('div', { class: 'gpx-fr__field' }, [
        el('span', { class: 'gpx-fr__label', text: 'الجنس' }),
        genderGroup,
      ]));

      var errors = validateProfile(state.profile);

      function numberField(labelText, key, placeholder) {
        // Only actually show the invalid (red) state once the shopper has
        // typed something wrong, or after they tried "التالي" with it still
        // empty (state.forceShowErrors) — never on first paint, when every
        // field is legitimately still blank. Matches the error-text gating
        // just below; previously this used `errors[key]` alone, which is
        // true for an untouched blank field too, so all three inputs opened
        // red before the shopper had done anything (real bug, not a style
        // choice — reported against modal_mobile.png 2026-09-16).
        var showInvalid = !!errors[key] && (state.profile[key] !== '' || state.forceShowErrors);
        var input = el('input', {
          type: 'number', inputmode: 'numeric', class: 'gpx-fr__input',
          value: state.profile[key] || '', placeholder: placeholder,
          'aria-invalid': showInvalid ? 'true' : 'false',
          'aria-label': labelText,
        });
        input.addEventListener('input', function () {
          state.profile[key] = input.value;
        });
        var errorEl = el('div', { class: 'gpx-fr__error', role: 'alert' });
        if (errors[key] && state.profile[key] !== '') errorEl.textContent = errors[key];
        return el('div', { class: 'gpx-fr__field' }, [
          el('label', { class: 'gpx-fr__label', text: labelText }),
          input,
          errorEl,
        ]);
      }

      var row = el('div', { class: 'gpx-fr__row' }, [
        numberField('الطول (سم)', 'heightCm', 'مثلاً 170'),
        numberField('الوزن (كجم)', 'weightKg', 'مثلاً 70'),
      ]);
      wrap.appendChild(row);
      wrap.appendChild(numberField('السن (سنين)', 'age', 'مثلاً 28'));
      // rev5.2 (owner): fit-preference cards live in the step-1 area, after
      // height/weight/(body shape), before the mannequin/measurements step.
      wrap.appendChild(fitPreferencePicker());

      var actions = el('div', { class: 'gpx-fr__actions' });
      var nextBtn = el('button', { type: 'button', class: 'gpx-fr__btn gpx-fr__btn--primary', text: 'التالي' });
      nextBtn.addEventListener('click', function () {
        var currentErrors = validateProfile(state.profile);
        if (Object.keys(currentErrors).length > 0) {
          state.forceShowErrors = true;
          render();
          return;
        }
        state.profile.heightCm = Number(state.profile.heightCm);
        state.profile.weightKg = Number(state.profile.weightKg);
        state.profile.age = Number(state.profile.age);
        computeEstimateAndSeedCorrections();
        state.step = 2;
        render();
      });
      actions.appendChild(nextBtn);
      wrap.appendChild(actions);

      if (state.forceShowErrors) {
        var errs = validateProfile(state.profile);
        Object.keys(errs).forEach(function (key) {
          var input = wrap.querySelector('[aria-label="' + (key === 'heightCm' ? 'الطول (سم)' : key === 'weightKg' ? 'الوزن (كجم)' : 'السن (سنين)') + '"]');
          if (input) {
            input.setAttribute('aria-invalid', 'true');
            var field = input.closest ? input.closest('.gpx-fr__field') : input.parentNode;
            var errorEl = field && field.querySelector('.gpx-fr__error');
            if (errorEl) errorEl.textContent = errs[key];
          }
        });
      }

      return wrap;
    }

    var MEASURE_ZONE_LABEL = { chest: 'الصدر', waist: 'الوسط / الخصر', hip: 'الهيب' };
    // Egyptian-Arabic helper line under the mannequin, one per zone —
    // matches the Sizebay reference ("حدد صدرك بشريط قياس وتنفس بشكل
    // طبيعي") and swaps with whichever slider is currently active.
    var MEASURE_HELPER_TEXT = {
      chest: 'حدد صدرك بشريط قياس وتنفس بشكل طبيعي',
      waist: 'لف الشريط حوالين أضيق نقطة في وسطك من غير ما تشده',
      hip: 'لف الشريط حوالين أوسع نقطة في الهيب',
    };

    // rev5.2 (owner, verbatim: «وخلي فيه التحديد انت بتفضل اسليم فت ولا
    // بوكس فيت ولا واسع ولا ضيق ولا ايه») — 4 cards in the step-1 area,
    // one tap each, default 'مظبوط' ("عادي"). Internal VALUES stay the
    // same 4 EASE_TARGETS/FIT_PREFS keys recommend() already validates
    // (ضيق/مظبوط/واسع/أوفر سايز) — only the visible LABEL combines both
    // Arabic terms Ahmed asked for; no separate label->key mapping layer.
    // A small single-path line icon per card (brand accent color, same
    // stroke weight as the header icons) instead of the old plain-text
    // pill picker this replaces (that picker was defined but never wired
    // into any screen — see the rev4 note in renderStep2's own comment).
    var FIT_PREF_CARDS = [
      { value: 'ضيق', label: 'ضيق / سليم فت', icon: 'M8 12h8M8 12l3-3M8 12l3 3M16 12l-3-3M16 12l-3 3' },
      { value: 'مظبوط', label: 'عادي / ريجولار', icon: 'M12 5v14M9 8l3-3 3 3M9 16l3 3 3-3' },
      { value: 'واسع', label: 'واسع / بوكس فيت', icon: 'M4 12h16M4 12l3-3M4 12l3 3M20 12l-3-3M20 12l-3 3' },
      { value: 'أوفر سايز', label: 'أوفر سايز', icon: 'M3 12h18M3 12l4-4M3 12l4 4M21 12l-4-4M21 12l-4 4M12 4v2M12 18v2' },
    ];

    function fitPreferencePicker() {
      var group = el('div', { class: 'gpx-fr__fitpref-grid', role: 'radiogroup', 'aria-label': 'حابب المقاس يكون' });
      FIT_PREF_CARDS.forEach(function (card) {
        var pressed = state.fitPreference === card.value;
        var svgNS = 'http://www.w3.org/2000/svg';
        var svg = document.createElementNS(svgNS, 'svg');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('width', '22');
        svg.setAttribute('height', '22');
        svg.setAttribute('fill', 'none');
        svg.setAttribute('stroke', 'currentColor');
        svg.setAttribute('stroke-width', '1.8');
        svg.setAttribute('stroke-linecap', 'round');
        svg.setAttribute('stroke-linejoin', 'round');
        svg.setAttribute('aria-hidden', 'true');
        var path = document.createElementNS(svgNS, 'path');
        path.setAttribute('d', card.icon);
        svg.appendChild(path);
        var btn = el('button', {
          type: 'button', class: 'gpx-fr__fitpref-card', 'aria-pressed': pressed ? 'true' : 'false',
        }, [svg, el('span', { text: card.label })]);
        btn.addEventListener('click', function () {
          state.fitPreference = card.value;
          render();
        });
        group.appendChild(btn);
      });
      return el('div', { class: 'gpx-fr__field' }, [
        el('span', { class: 'gpx-fr__label', text: 'حابب المقاس يكون' }),
        group,
      ]);
    }

    // renderStep2 — "شكل جسمك": a Sizebay-style growing/shrinking mannequin
    // (SVG, left/right depending on RTL) next to a slider + numeric cm box
    // per zone (chest/waist always, hip for women only — see
    // correctionZonesForGender). The zone currently being edited gets a
    // highlighted band drawn across the mannequin at that height, plus a
    // one-line Egyptian-Arabic helper underneath. Dragging a slider only
    // patches the SVG path/band/number-box attributes in place (updateVisual)
    // instead of calling the full render() — a full teardown+rebuild of the
    // DOM node being dragged would interrupt the drag/pointer capture on a
    // real touch device, which a naive "re-render on every input event"
    // implementation would do.
    function renderStep2() {
      var wrap = el('div', { class: 'gpx-fr__step gpx-fr__step--measure' });
      wrap.appendChild(el('h3', { class: 'gpx-fr__step-heading', text: 'أدخل قياساتك' }));
      wrap.appendChild(el('p', { class: 'gpx-fr__measure-intro', text: 'قيّم القياسات دي وصحّحها لو مختلفة عن جسمك — كل ما تقرّب من قياسك الحقيقي كل ما المقاس يطلع أدق.' }));

      if (!state.estimate) computeEstimateAndSeedCorrections();

      var zones = correctionZonesForGender(state.profile.gender);
      if (zones.indexOf(state.activeZone) === -1) state.activeZone = zones[0];

      var measureGrid = el('div', { class: 'gpx-fr__measure-grid' });

      // ---- mannequin column ----
      // Multi-part SVG (2026-09-16 rebuild): torso/arms/legs are separate
      // shapes from buildMannequinShapes() so limbs read as limbs, filled
      // with a soft light-grey linear gradient (not flat white) for subtle
      // shading, on the cream modal background. Feet are plain ellipses.
      // Paint order matters: legs, then arms, then torso (torso overlaps
      // the arm/leg attachment points so there's no visible seam), then
      // neck+head on top. The band + its two arrowheads (Sizebay-style
      // "<-- zone -->" caliper marks) are drawn last so they sit above
      // everything.
      var svgNS = 'http://www.w3.org/2000/svg';
      var gradId = 'gpxFrGrad' + Math.random().toString(36).slice(2, 9);
      var mannCol = el('div', { class: 'gpx-fr__mannequin-col' });
      var svg = document.createElementNS(svgNS, 'svg');
      svg.setAttribute('viewBox', '0 0 ' + MANNEQUIN_VIEW.w + ' ' + MANNEQUIN_VIEW.h);
      svg.setAttribute('class', 'gpx-fr__mannequin');
      svg.setAttribute('role', 'img');
      svg.setAttribute('aria-label', 'شكل جسمك بيتغيّر مع القياسات');

      var defs = document.createElementNS(svgNS, 'defs');
      var grad = document.createElementNS(svgNS, 'linearGradient');
      grad.setAttribute('id', gradId);
      grad.setAttribute('x1', '0'); grad.setAttribute('y1', '0');
      grad.setAttribute('x2', '1'); grad.setAttribute('y2', '0');
      [['0%', '#f1efec'], ['55%', '#e4e0da'], ['100%', '#d3cdc4']].forEach(function (s) {
        var stop = document.createElementNS(svgNS, 'stop');
        stop.setAttribute('offset', s[0]);
        stop.setAttribute('stop-color', s[1]);
        grad.appendChild(stop);
      });
      defs.appendChild(grad);
      svg.appendChild(defs);

      function bodyShape(tag) {
        var node = document.createElementNS(svgNS, tag);
        node.setAttribute('class', 'gpx-fr__mannequin-part');
        node.setAttribute('fill', 'url(#' + gradId + ')');
        return node;
      }

      var legLeftEl = bodyShape('path');
      var legRightEl = bodyShape('path');
      var footLeftEl = bodyShape('ellipse');
      var footRightEl = bodyShape('ellipse');
      var armLeftEl = bodyShape('path');
      var armRightEl = bodyShape('path');
      var torsoEl = bodyShape('path');
      var neckEl = bodyShape('rect');
      neckEl.setAttribute('rx', '3');
      var headEl = bodyShape('circle');
      headEl.setAttribute('r', '12');

      var bandRect = document.createElementNS(svgNS, 'rect');
      bandRect.setAttribute('class', 'gpx-fr__mannequin-band');
      bandRect.setAttribute('rx', '7');
      var arrowLeft = document.createElementNS(svgNS, 'polygon');
      arrowLeft.setAttribute('class', 'gpx-fr__mannequin-arrow');
      var arrowRight = document.createElementNS(svgNS, 'polygon');
      arrowRight.setAttribute('class', 'gpx-fr__mannequin-arrow');

      [footLeftEl, footRightEl, legLeftEl, legRightEl, armLeftEl, armRightEl, torsoEl, neckEl, headEl, bandRect, arrowLeft, arrowRight]
        .forEach(function (node) { svg.appendChild(node); });

      // ---- FIT-3D stage (2026-09-16) ----
      // `svg` above stays exactly as before — it's the instant, always-
      // correct, unit-tested render and the automatic fallback. `host3d`
      // is an empty box stacked on top of it (same aspect box, see
      // gpx-fit-room.css's .gpx-fr__mannequin-stage) that only becomes
      // visible once window.GpxFitRoom3D actually mounts a working three.js
      // scene into it — see attemptMount3D()/updateVisual() below. If
      // WebGL is missing, the CDN is slow/blocked, or anything throws,
      // host3d simply never un-hides and the shopper sees the SVG exactly
      // like before this feature existed.
      var stage = el('div', { class: 'gpx-fr__mannequin-stage' });
      stage.appendChild(svg);
      var host3d = el('div', { class: 'gpx-fr__mannequin3d' });
      host3d.hidden = true;
      stage.appendChild(host3d);

      // ---- rev6 style switcher (owner's build order, verbatim: «استخدم
      // برضو دمية الخياطة ويكون في في الجنب كدا اختيارات انه يغير شكل
      // المانيكان») — a small vertical chip/icon list at the SIDE of the
      // mannequin on desktop (gpx-fr__mannequin-row, RTL-aware: DOM order
      // puts it first, which sits on the visual RIGHT next to the stage
      // in this RTL layout, same side the trigger/measure-grid already
      // reads from), collapsing to a horizontal row UNDER the mannequin
      // on mobile — see gpx-fit-room.css's max-width rule for
      // `.gpx-fr__mannequin-row`. Only touches the LIVE 3D controller
      // when one is mounted; if the shopper is on the SVG fallback (no
      // WebGL), clicking a chip still updates state + persists it, it
      // just has nothing visual to apply to until FIT-3D loads (or the
      // next product page, via the saved profile prefill).
      // rev7 (2026-09-20, owner: «شيل تغييرات الالوان») — the sibling
      // colour switcher (6 swatches) that used to live right here is
      // REMOVED. The mannequin colour is fixed (white) in
      // gpx-fit-room-3d.js and not user-controllable at all any more.
      var styleSwitcher = el('div', { class: 'gpx-fr__style-switcher', role: 'radiogroup', 'aria-label': 'شكل المانيكان' });
      var styleButtons = {};
      MANNEQUIN_STYLES.forEach(function (styleDef) {
        var pressed = state.mannequinStyle === styleDef.key;
        var btn = el('button', {
          type: 'button', class: 'gpx-fr__style-chip' + (pressed ? ' is-active' : ''),
          'aria-pressed': String(pressed), title: styleDef.label,
        }, [el('span', { class: 'gpx-fr__style-chip-icon', html: styleDef.icon }), el('span', { class: 'gpx-fr__style-chip-label', text: styleDef.label })]);
        btn.addEventListener('click', function () {
          if (state.mannequinStyle === styleDef.key) return;
          state.mannequinStyle = styleDef.key;
          Object.keys(styleButtons).forEach(function (k) {
            styleButtons[k].classList.toggle('is-active', k === styleDef.key);
            styleButtons[k].setAttribute('aria-pressed', String(k === styleDef.key));
          });
          if (mannequin3D && mannequin3D.setStyle) mannequin3D.setStyle(styleDef.key);
          persistMannequinPrefs();
        });
        styleButtons[styleDef.key] = btn;
        styleSwitcher.appendChild(btn);
      });

      var switcherCol = el('div', { class: 'gpx-fr__mannequin-switchers' }, [styleSwitcher]);
      var mannRow = el('div', { class: 'gpx-fr__mannequin-row' }, [switcherCol, stage]);
      mannCol.appendChild(mannRow);
      var helperText = el('p', { class: 'gpx-fr__measure-helper', 'aria-live': 'polite' });
      mannCol.appendChild(helperText);
      measureGrid.appendChild(mannCol);

      // ---- controls column ----
      var controlsCol = el('div', { class: 'gpx-fr__controls-col' });
      var rowsByZone = {};

      function currentMeasures() {
        return {
          chest: Number(state.corrections.chest),
          waist: Number(state.corrections.waist),
          hip: state.corrections.hip !== undefined && state.corrections.hip !== ''
            ? Number(state.corrections.hip)
            : (state.estimate ? state.estimate.hip : undefined),
          heightCm: state.profile.heightCm,
        };
      }

      function updateVisual() {
        var metrics = mannequinMetrics(state.profile.gender, currentMeasures());
        var shapes = buildMannequinShapes(metrics);
        legLeftEl.setAttribute('d', shapes.legLeft);
        legRightEl.setAttribute('d', shapes.legRight);
        armLeftEl.setAttribute('d', shapes.armLeft);
        armRightEl.setAttribute('d', shapes.armRight);
        torsoEl.setAttribute('d', shapes.torso);

        var footL = mannequinFootEllipse(metrics, -1);
        var footR = mannequinFootEllipse(metrics, 1);
        footLeftEl.setAttribute('cx', String(footL.cx)); footLeftEl.setAttribute('cy', String(footL.cy));
        footLeftEl.setAttribute('rx', String(footL.rx)); footLeftEl.setAttribute('ry', String(footL.ry));
        footRightEl.setAttribute('cx', String(footR.cx)); footRightEl.setAttribute('cy', String(footR.cy));
        footRightEl.setAttribute('rx', String(footR.rx)); footRightEl.setAttribute('ry', String(footR.ry));

        neckEl.setAttribute('x', String(metrics.cx - 7));
        neckEl.setAttribute('y', '30');
        neckEl.setAttribute('width', '14');
        neckEl.setAttribute('height', '16');
        headEl.setAttribute('cx', String(metrics.cx));
        headEl.setAttribute('cy', '18');

        var hwKey = state.activeZone + 'HW';
        var hw = metrics[hwKey] != null ? metrics[hwKey] : metrics.chestHW;
        var zoneYVal = metrics.zoneY[state.activeZone] != null ? metrics.zoneY[state.activeZone] : metrics.zoneY.chest;
        var bandHeight = 24;
        var bandLeftX = metrics.cx - hw - 7;
        var bandRightX = metrics.cx + hw + 7;
        var bandCenterY = zoneYVal;
        bandRect.setAttribute('x', String(bandLeftX));
        bandRect.setAttribute('y', String(zoneYVal - bandHeight / 2));
        bandRect.setAttribute('width', String(bandRightX - bandLeftX));
        bandRect.setAttribute('height', String(bandHeight));

        // Caliper arrowheads at each end of the band, pointing outward —
        // the "<-- this wide -->" mark from Ahmed's Sizebay reference.
        var arrowLen = 7;
        var arrowHalfH = 5;
        arrowLeft.setAttribute('points',
          (bandLeftX) + ',' + bandCenterY + ' ' +
          (bandLeftX + arrowLen) + ',' + (bandCenterY - arrowHalfH) + ' ' +
          (bandLeftX + arrowLen) + ',' + (bandCenterY + arrowHalfH));
        arrowRight.setAttribute('points',
          (bandRightX) + ',' + bandCenterY + ' ' +
          (bandRightX - arrowLen) + ',' + (bandCenterY - arrowHalfH) + ' ' +
          (bandRightX - arrowLen) + ',' + (bandCenterY + arrowHalfH));

        helperText.textContent = MEASURE_HELPER_TEXT[state.activeZone] || '';

        if (mannequin3D) {
          try { mannequin3D.render(metrics, state.activeZone); } catch (e) { /* SVG above is already correct regardless */ }
        }
      }

      // ---- FIT-3D mount attempt — only now (step 2 is actually open),
      // never on page load. Silently gives up (host3d stays hidden,
      // mannequin3D stays null, SVG is what the shopper sees) on ANY
      // failure: no WebGL, three.js didn't load within its 4s budget, or
      // a runtime exception building the scene. Guarded by the
      // page-level gpxfr3dDisabled flag so one failure doesn't retry a
      // doomed CDN fetch every time step 2 re-renders.
      function attemptMount3D() {
        if (gpxfr3dDisabled || !window.GpxFitRoom3D) { gpxfr3dDisabled = true; return; }
        var isFemale = state.profile.gender === 'أنثى' || state.profile.gender === 'female';
        window.GpxFitRoom3D.mount(host3d, {
          gender: isFemale ? 'female' : 'male',
          style: state.mannequinStyle,
        }).then(function (controller) {
          if (!controller) { gpxfr3dDisabled = true; return; }
          if (state.step !== 2) { try { controller.destroy(); } catch (e) { /* noop */ } return; } // stepped away while awaiting
          mannequin3D = controller;
          // Covers the (rare) race where the shopper clicked a style
          // chip WHILE the CDN load above was still in flight — the mount
          // options captured the state at call time; re-applying here is a
          // cheap no-op if nothing changed (applyStyle is idempotent) and
          // correct if something did. Colour is fixed (always white) in
          // gpx-fit-room-3d.js now, so there's no setColor call here any
          // more — see the rev7 note near DEFAULT_MANNEQUIN_STYLE above.
          mannequin3D.setStyle(state.mannequinStyle);
          // Test-only seam (harmless in production — a single object
          // reference, no behavior change): lets an automated check call
          // controller.__debugZoneCheck(zone) from outside this closure
          // without needing to touch gpx-fit-room-3d.js's own internals.
          window.__gpxFrDebugController = controller;
          // rev4 fix: `svg.hidden = true` alone doesn't actually hide it —
          // .gpx-fr__mannequin's own `display:block` rule (same
          // specificity as the browser's built-in `[hidden]{display:
          // none}`) wins on source order, so the SVG fallback stayed
          // visually rendered UNDER the (transparent-background) 3D
          // canvas. Invisible before with the old full-body camera crop
          // (the SVG's shapes roughly matched/hid behind the 3D figure's
          // own silhouette); very visible now with the new tight head-
          // to-thigh crop (rev4, Sizebay-match), where the SVG's still
          // full-body-scaled head/shoulder shapes stick out from behind
          // the 3D head. Fixed with an inline style, which beats any
          // external stylesheet rule regardless of cascade order.
          svg.hidden = true;
          svg.style.display = 'none';
          host3d.hidden = false;
          updateVisual();
        });
      }
      if (!gpxfr3dDisabled) {
        if (window.GpxFitRoom3D) attemptMount3D();
        else loadMannequin3DScript(attemptMount3D);
      }

      function setActiveZone(zone) {
        state.activeZone = zone;
        zones.forEach(function (z) {
          if (rowsByZone[z]) rowsByZone[z].classList.toggle('is-active', z === zone);
        });
        updateVisual();
      }

      zones.forEach(function (zone) {
        var bounds = MEASURE_BOUNDS[zone];
        var seed = state.estimate ? Math.round(state.estimate[zone]) : bounds[0];
        var current = state.corrections[zone];
        var value = (current === undefined || current === null || current === '') ? seed : clampMeasurement(zone, current);
        state.corrections[zone] = value;

        var slider = el('input', {
          type: 'range', min: String(bounds[0]), max: String(bounds[1]), step: '1',
          value: String(value), class: 'gpx-fr__slider',
          'aria-label': MEASURE_ZONE_LABEL[zone] + ' بالسم',
        });
        var numberBox = el('input', {
          type: 'number', inputmode: 'numeric', min: String(bounds[0]), max: String(bounds[1]),
          value: String(value), class: 'gpx-fr__input gpx-fr__input--measure',
          'aria-label': MEASURE_ZONE_LABEL[zone] + ' بالسم (رقم)',
        });

        // rev5.1 (real-staging review): --gpx-fr-slider-pct drives the
        // track's own "filled up to here" gradient stop (see
        // gpx-fit-room.css) — accent-color alone doesn't reliably paint a
        // fill once a global input reset has already touched the
        // track/thumb, so the fill position is computed here instead.
        function syncSliderFillPct(num) {
          var pct = ((num - bounds[0]) / (bounds[1] - bounds[0])) * 100;
          slider.style.setProperty('--gpx-fr-slider-pct', Math.max(0, Math.min(100, pct)) + '%');
        }
        syncSliderFillPct(value);

        function onZoneInput(rawValue) {
          var num = clampMeasurement(zone, rawValue);
          state.corrections[zone] = num;
          if (slider.value !== String(num)) slider.value = String(num);
          if (numberBox.value !== String(num)) numberBox.value = String(num);
          syncSliderFillPct(num);
          setActiveZone(zone);
        }

        slider.addEventListener('input', function () { onZoneInput(slider.value); });
        slider.addEventListener('focus', function () { setActiveZone(zone); });
        numberBox.addEventListener('input', function () { onZoneInput(numberBox.value); });
        numberBox.addEventListener('focus', function () { setActiveZone(zone); });

        var row = el('div', { class: 'gpx-fr__measure-row' + (zone === state.activeZone ? ' is-active' : '') });
        row.appendChild(el('div', { class: 'gpx-fr__measure-row-head' }, [
          el('span', { class: 'gpx-fr__label', text: MEASURE_ZONE_LABEL[zone] }),
          numberBox,
          el('span', { class: 'gpx-fr__measure-unit', text: 'سم' }),
        ]));
        row.appendChild(slider);
        rowsByZone[zone] = row;
        controlsCol.appendChild(row);
      });

      // rev4 (Sizebay-parity, 2026-09-16): the fit-preference pill picker
      // ("حابب المقاس يكون ضيق/مظبوط/واسع") isn't part of Ahmed's exact
      // reference layout (title -> subtext -> per-zone rows -> two
      // buttons, nothing else) — moved out of THIS screen rather than
      // deleted outright; state.fitPreference still defaults to 'مظبوط'
      // and recommend() still reads it, so behaviour is unchanged, only
      // the extra UI is gone from the measure step to match the
      // reference 1:1.
      measureGrid.appendChild(controlsCol);
      wrap.appendChild(measureGrid);

      updateVisual();

      // Two buttons at the bottom of the LEFT (controls) column, per the
      // reference: primary "عرض التوصية" ("show the recommendation"),
      // secondary "ابدأ من جديد" ("start over").
      // rev5.2 (owner): «ابدأ من جديد» now clears the step-1 prefill too
      // and returns to step 1 — a real fresh start, not just a corrections
      // reset that stayed on step 2.
      var actions = el('div', { class: 'gpx-fr__actions' });
      var backBtn = el('button', { type: 'button', class: 'gpx-fr__btn gpx-fr__btn--ghost', text: 'ابدأ من جديد' });
      backBtn.addEventListener('click', function () {
        state.profile = { gender: 'ذكر', heightCm: '', weightKg: '', age: '' };
        state.corrections = {};
        state.estimate = null;
        state.fitPreference = 'مظبوط';
        // rev6 — mannequinStyle deliberately NOT reset here: it's a
        // display preference (which mannequin look the shopper likes),
        // not measurement/profile data — "a real fresh start" per rev5.2
        // above is about the body inputs, not about making the shopper
        // re-pick دمية خياط every time they hit "ابدأ من جديد" mid-session.
        // (rev7: colour is no longer a preference at all — always white.)
        state.forceShowErrors = false;
        state.step = 1;
        render();
      });
      var goBtn = el('button', { type: 'button', class: 'gpx-fr__btn gpx-fr__btn--primary', text: 'عرض التوصية' });
      goBtn.addEventListener('click', function () {
        try {
          var baseBody = state.estimate || estimateBody({
            gender: state.profile.gender,
            heightCm: state.profile.heightCm,
            weightKg: state.profile.weightKg,
            age: state.profile.age,
          });
          // The shopper's corrected chest/waist/(hip) OVERRIDE the ANSUR
          // estimate here — this is the one call site that decides what
          // recommend() actually scores against. See engine.js's
          // applyMeasurementCorrections() doc comment.
          var body = applyMeasurementCorrections(baseBody, state.corrections);
          var garment = chart.garment || 'set';
          var result = recommend(body, chart, garment, state.fitPreference, availabilityBySize);
          state.result = result;
          state.resultBody = body; // FIT-3D result mannequin (renderStep3) needs the actual body, recommend() doesn't return it
          safeSetProfile({
            gender: state.profile.gender,
            heightCm: state.profile.heightCm,
            weightKg: state.profile.weightKg,
            age: state.profile.age,
            corrections: state.corrections,
            fitPreference: state.fitPreference,
            mannequinStyle: state.mannequinStyle,
          });
          state.step = 3;
          render();
        } catch (err) {
          state.result = null;
          state.step = 3;
          state.resultError = 'معنداش بيانات كافية نحسب عليها مقاسك — جرّب تعدّل بياناتك.';
          render();
        }
      });
      actions.appendChild(goBtn);
      actions.appendChild(backBtn);
      controlsCol.appendChild(actions);
      return wrap;
    }

    function findVariantForSize(sizeName) {
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].size && variants[i].size.trim().toUpperCase() === String(sizeName).trim().toUpperCase()) {
          return variants[i];
        }
      }
      return null;
    }

    function selectVariant(sizeName) {
      var variant = findVariantForSize(sizeName);
      if (!variant) return;
      // Standard Shopify variant-select event contract: update the hidden
      // variant <select>/radio input on the product form if present, else
      // fall back to a straight location change with the variant param
      // (works on any theme, always a safe no-op if a JS-driven picker
      // intercepts it first).
      var form = root.closest('.t4s-product-form, form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
      var input = form && form.querySelector('input[name="id"], select[name="id"]');
      if (input) {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      document.dispatchEvent(new CustomEvent('gpxFitRoom:selectVariant', { detail: { variantId: variant.id, size: sizeName } }));
      closeModal();
    }

    function zoneRow(zoneKey, zoneData) {
      var verdictClass = 'gpx-fr__zone-verdict--' + zoneData.verdict;
      return el('div', { class: 'gpx-fr__zone-row' }, [
        el('span', { text: zoneData.label }),
        el('span', { class: 'gpx-fr__zone-verdict ' + verdictClass, text: zoneData.verdict }),
      ]);
    }

    function renderStep3() {
      var wrap = el('div', { class: 'gpx-fr__step' });
      wrap.appendChild(el('h3', { class: 'gpx-fr__step-heading', text: 'النتيجة' }));

      if (!state.result) {
        wrap.appendChild(el('p', { class: 'gpx-fr__result-note', text: state.resultError || 'حصل خطأ.' }));
        var backOnly = el('button', { type: 'button', class: 'gpx-fr__btn gpx-fr__btn--ghost', text: 'رجوع' });
        backOnly.addEventListener('click', function () { state.step = 2; render(); });
        wrap.appendChild(backOnly);
        return wrap;
      }

      var result = state.result;
      // result.available/result.outOfStock are now the single source of
      // truth (2026-09-16 fix) — recommend() already resolved them against
      // the SAME live per-size availability map used to build the variant
      // list below, so there is no second, possibly-inconsistent check
      // here anymore. See fitting_room/engine.js's recommend() doc comment.

      wrap.appendChild(el('div', { class: 'gpx-fr__result-head' }, [
        el('span', { class: 'gpx-fr__result-label', text: 'مقاسك المقترح:' }),
        el('span', { class: 'gpx-fr__result-size', text: result.size }),
      ]));

      // rev5.2 (owner): when the shopper picked a non-regular fit
      // preference, name it explicitly + show what 'عادي' would have
      // been — recommend()'s own alternateRegularSize (identical
      // sold-out/availability logic, not a second guess).
      if (result.fitPreference && result.fitPreference !== 'مظبوط') {
        var fitPrefDisplayLabel = { 'ضيق': 'سليم فت', 'واسع': 'بوكس فيت', 'أوفر سايز': 'أوفر سايز' }[result.fitPreference] || result.fitPreference;
        wrap.appendChild(el('p', {
          class: 'gpx-fr__result-note gpx-fr__result-note--pref',
          text: 'على تفضيلك (' + fitPrefDisplayLabel + '): مقاسك ' + result.size,
        }));
        if (result.alternateRegularSize) {
          wrap.appendChild(el('p', {
            class: 'gpx-fr__result-note gpx-fr__result-note--pref',
            text: 'لو عايزه عادي: ' + result.alternateRegularSize,
          }));
        }
      }

      // ---- FIT-3D result mannequin (2026-09-16) — "beat Sizebay": shows
      // the SAME body wearing a translucent garment outline colored per
      // zone by its actual fit verdict (ضيق=red/ مظبوط=green/ واسع=grey),
      // idle-rotating so the shopper can turn it. Pure enhancement, no
      // SVG twin — step 3 had NO mannequin visual before this feature, so
      // on ANY failure (no WebGL, CDN blocked/slow, a build exception)
      // NOTHING is ever inserted into the DOM — zero footprint, not a
      // blank reserved box — degrading to exactly the same "no mannequin
      // here" step 3 already had before this feature existed. The stage
      // node is only appended (right before the zones list, via this
      // anchor) once mount() has actually succeeded, so a failure never
      // even reserves layout space. Needs a real body (state.resultBody,
      // set by step 2's "اعرف مقاسي" handler above) and at least one
      // scored zone.
      var resultMannequinAnchor = document.createComment('gpx-fr-result-mannequin');
      wrap.appendChild(resultMannequinAnchor);
      if (state.resultBody && result.zones && !gpxfr3dDisabled) {
        var resultMetrics = mannequinMetrics(state.profile.gender, {
          chest: state.resultBody.chest, waist: state.resultBody.waist,
          hip: state.resultBody.hip, heightCm: state.resultBody.heightCm,
        });

        function attemptMountResult3D() {
          if (gpxfr3dDisabled || !window.GpxFitRoom3D) { gpxfr3dDisabled = true; return; }
          var resultHost = el('div', { class: 'gpx-fr__mannequin3d gpx-fr__mannequin3d--result' });
          var resultIsFemale = state.profile.gender === 'أنثى' || state.profile.gender === 'female';
          window.GpxFitRoom3D.mount(resultHost, {
            gender: resultIsFemale ? 'female' : 'male',
            style: state.mannequinStyle,
          }).then(function (controller) {
            if (!controller) { gpxfr3dDisabled = true; return; }
            // stepped away, or this exact result screen was torn down
            // (re-render), while the mount was in flight
            if (state.step !== 3 || !resultMannequinAnchor.parentNode) {
              try { controller.destroy(); } catch (e) { /* noop */ }
              return;
            }
            var resultStage = el('div', { class: 'gpx-fr__mannequin-stage gpx-fr__mannequin-stage--result' }, [resultHost]);
            resultMannequinAnchor.parentNode.insertBefore(resultStage, resultMannequinAnchor);
            mannequin3D = controller;
            controller.renderResult(resultMetrics, result.zones);
          });
        }
        if (window.GpxFitRoom3D) attemptMountResult3D();
        else loadMannequin3DScript(attemptMountResult3D);
      }

      var zonesWrap = el('div', { class: 'gpx-fr__zones' });
      ZONE_ORDER.forEach(function (zoneKey) {
        if (result.zones[zoneKey]) zonesWrap.appendChild(zoneRow(zoneKey, result.zones[zoneKey]));
      });
      wrap.appendChild(zonesWrap);

      wrap.appendChild(el('p', { class: 'gpx-fr__result-note', text: result.note }));

      if (coverageGap.hasGap) {
        wrap.appendChild(el('p', {
          class: 'gpx-fr__result-note gpx-fr__result-note--gap',
          text: 'تنبيه: جدول المقاسات بتاع المنتج ده لسه مش شامل مقاس ' + coverageGap.missingSizes.join('، ') +
            ' — لو ده أقرب لمقاسك الحقيقي بدل ' + result.size + '، تواصل معانا وهنساعدك تتأكد قبل ما تطلب.',
        }));
      }

      if (result.outOfStock) {
        // NOTHING is available across the whole chart — do not offer any
        // size selection at all (no primary button, no runner-up button).
        wrap.appendChild(el('div', { class: 'gpx-fr__soldout', text: 'المنتج ده خلص من المخزون في كل المقاسات حالياً.' }));
        var editOnly = el('button', { type: 'button', class: 'gpx-fr__btn gpx-fr__btn--ghost', text: 'عدّل بياناتي' });
        editOnly.addEventListener('click', function () { state.step = 1; render(); });
        wrap.appendChild(editOnly);
        return wrap;
      }

      var actions = el('div', { class: 'gpx-fr__actions' });
      var selectBtn = el('button', {
        type: 'button', class: 'gpx-fr__btn gpx-fr__btn--primary',
        text: 'اختار مقاس ' + result.size,
      });
      selectBtn.addEventListener('click', function () { selectVariant(result.size); });
      var retryBtn = el('button', { type: 'button', class: 'gpx-fr__btn gpx-fr__btn--ghost', text: 'جرّب مقاس تاني' });
      retryBtn.addEventListener('click', function () { state.step = 2; render(); });
      actions.appendChild(retryBtn);
      actions.appendChild(selectBtn);
      wrap.appendChild(actions);

      if (result.runnerUp) {
        // Guaranteed available by recommend() itself — never a sold-out
        // size (2026-09-16 fix).
        var runnerBtn = el('button', {
          type: 'button', class: 'gpx-fr__btn gpx-fr__btn--ghost',
          text: 'أو جرّب مقاس ' + result.runnerUp,
        });
        runnerBtn.addEventListener('click', function () { selectVariant(result.runnerUp); });
        wrap.appendChild(runnerBtn);
      }

      var editBtn = el('button', { type: 'button', class: 'gpx-fr__btn gpx-fr__btn--ghost', text: 'عدّل بياناتي' });
      editBtn.addEventListener('click', function () { state.step = 1; render(); });
      wrap.appendChild(editBtn);
      wrap.appendChild(el('p', { class: 'gpx-fr__saved-note', text: 'بياناتك محفوظة على متصفحك بس — هتظهر تلقائي في أي منتج تاني.' }));

      return wrap;
    }

    function render() {
      destroyMannequin3D(); // never leave a WebGL context behind bodyEl.innerHTML='' below
      bodyEl.innerHTML = '';
      var content;
      if (state.step === 1) content = renderStep1();
      else if (state.step === 2) content = renderStep2();
      else content = renderStep3();
      bodyEl.appendChild(content);
    }
  }

  function boot() {
    var roots = document.querySelectorAll('[data-gpx-fr]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Expose for the preview harness / manual testing in the console.
  window.GpxFitRoom = {
    estimateBody: estimateBody,
    recommend: recommend,
    chartCoverageGap: chartCoverageGap,
    clampMeasurement: clampMeasurement,
    correctionZonesForGender: correctionZonesForGender,
    applyMeasurementCorrections: applyMeasurementCorrections,
    seedCorrectionsFromEstimate: seedCorrectionsFromEstimate,
    mannequinMetrics: mannequinMetrics,
    buildMannequinShapes: buildMannequinShapes,
    mannequinFootEllipse: mannequinFootEllipse,
  };
})();
